import type {
  RecruitingSiteResponse,
  SiteJobDescriptionPublicDetailResponse,
} from '@flex-apis/recruiting';
import { JobDescriptionStatus, PageType } from '@flex-apis/recruiting';
import { fetchI18nResources, getTranslationsProps } from '@flex-packages/i18n';
import { serverSideTranslations } from '@flex-packages/i18n/next/serverSideTranslations';
import {
  generatePublicJobDescriptionDetail,
  generateSiteDesign,
} from '@flex-private-packages/recruiting-site-builder/base';
import type { GetServerSideProps } from 'next';

import type { WithErrorProps } from '../../../src';
import {
  fetchJobDescriptionForPublic,
  fetchRecruitingSite,
  getSubdomainFromServerSidePropsContext,
  getUrlQueryParamFromServerSidePropsContext,
  handleServerSideError,
  handleSystemSubdomain,
  JobDescriptionDetailPage,
  useSendAccessLog,
  withErrorPage,
} from '../../../src';
import { WithLanguageDetection } from '../../../src/app/commons/WithLanguageDetection';
import { getServerAxios } from '../../../src/utils/axios';

interface Props {
  recruitingSiteResponse: RecruitingSiteResponse;
  siteJobDescriptionPublicDetailResponse: SiteJobDescriptionPublicDetailResponse;
}

function JobDescriptionDetail({
  recruitingSiteResponse,
  siteJobDescriptionPublicDetailResponse,
}: Props) {
  const siteDesign = generateSiteDesign(recruitingSiteResponse);
  const jobDescription = generatePublicJobDescriptionDetail(
    siteJobDescriptionPublicDetailResponse
  );

  useSendAccessLog({
    customerIdHash: siteDesign.customerIdHash,
    pageType: PageType.JobDescription,
  });

  return (
    <WithLanguageDetection useLoader={true}>
      <JobDescriptionDetailPage
        siteDesign={siteDesign}
        jobDescription={jobDescription}
      />
    </WithLanguageDetection>
  );
}

const JobDescriptionDetailWithErrorPage = withErrorPage(JobDescriptionDetail);
export default JobDescriptionDetailWithErrorPage;

type ServerSideProps = WithErrorProps<Props>;
export const getServerSideProps: GetServerSideProps<
  ServerSideProps
> = async context => {
  try {
    const axiosInstance = getServerAxios({
      req: context.req,
      res: context.res,
    });
    const subdomain = getSubdomainFromServerSidePropsContext(context);
    const jobDescriptionIdHash = getUrlQueryParamFromServerSidePropsContext(
      context,
      'jobDescriptionIdHash'
    );

    await handleSystemSubdomain({ context, axiosInstance, subdomain });
    const recruitingSiteResponse = await fetchRecruitingSite(axiosInstance, {
      subdomain,
    });

    const { customerIdHash } = recruitingSiteResponse.design;
    const [
      siteJobDescriptionPublicDetailResponse,
      { props: translationProps },
    ] = await Promise.all([
      fetchJobDescriptionForPublic(axiosInstance, {
        customerIdHash,
        jobDescriptionIdHash,
      }),
      getTranslationsProps(
        serverSideTranslations,
        fetchI18nResources(require.context('../../../locales'))
      )(context),
    ]);

    // 공고가 비공개일 경우 크롤링 삭제를 요청한다.
    if (
      siteJobDescriptionPublicDetailResponse.status ===
      JobDescriptionStatus.Private
    ) {
      context.res.statusCode = 404;
    }

    return {
      props: {
        siteJobDescriptionPublicDetailResponse,
        recruitingSiteResponse,
        ...translationProps,
      },
    };
  } catch (error) {
    return handleServerSideError(context, error);
  }
};
